import { DOCUMENT } from '@angular/common';
import {
  effect,
  EffectRef,
  inject,
  Injectable,
  Injector,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter, Subscription } from 'rxjs';

import type { FeatureAnnouncement } from '@customer-portal/data-access/feature-announcements';
import { FeatureAnnouncementStoreService } from '@customer-portal/data-access/feature-announcements';

import type { BadgeRecord, BadgeTarget } from '../interfaces';
import { createBadge, destroyBadge, updateBadge } from './badge/badge-dom';
import * as badgeTargetResolver from './badge/badge-target-resolver';
import { hideBadgeTooltip, showBadgeTooltip } from './badge/badge-tooltip';
import { FeatureAnnouncementCtaService } from './feature-announcement-cta.service';

@Injectable({ providedIn: 'root' })
export class FeatureAnnouncementBadgeRendererService implements OnDestroy {
  private readonly document = inject(DOCUMENT);
  private readonly announcementStore = inject(FeatureAnnouncementStoreService);
  private readonly ctaService = inject(FeatureAnnouncementCtaService);
  private readonly injector = inject(Injector);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly badges = new Map<HTMLElement, Map<string, BadgeRecord>>();
  private mutationObserver?: MutationObserver;
  private observedContent?: HTMLElement;
  private featureEffect?: EffectRef;
  private routeSubscription?: Subscription;
  private renderCancellation?: () => void;
  private tooltipCancellation?: () => void;
  private tooltipSequence = 0;
  private initialized = false;
  private destroyed = false;
  private hasUnseenBadgeTargets = false;

  initialize(): void {
    if (this.initialized || this.destroyed || !this.document.body) {
      return;
    }

    this.initialized = true;
    this.featureEffect = effect(
      () => {
        const unseenFeatures = this.announcementStore.unseenFeatures();
        this.hasUnseenBadgeTargets = unseenFeatures.some((feature) =>
          Boolean(feature.badgeTargets?.length),
        );
        this.observeContentMutations();
        this.scheduleRender();
      },
      { injector: this.injector },
    );
    this.routeSubscription = this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe(() => {
        this.observeContentMutations();
        this.scheduleRender();
      });

    this.observeContentMutations();

    const window = this.document.defaultView;
    this.ngZone.runOutsideAngular(() => {
      window?.addEventListener('scroll', this.handleScroll, true);
      window?.addEventListener('resize', this.handleResize);
    });
    this.scheduleRender();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.mutationObserver?.disconnect();
    this.routeSubscription?.unsubscribe();
    this.featureEffect?.destroy();
    this.cancelScheduledWork(this.renderCancellation);
    this.cancelScheduledWork(this.tooltipCancellation);
    this.renderCancellation = undefined;
    this.tooltipCancellation = undefined;

    const window = this.document.defaultView;
    window?.removeEventListener('scroll', this.handleScroll, true);
    window?.removeEventListener('resize', this.handleResize);
    this.forEachBadgeRecord((record) => destroyBadge(record));
    this.badges.clear();
  }

  private readonly handleScroll = (): void => this.hideTooltips();
  private readonly handleResize = (): void => this.scheduleTooltipRefresh();

  private observeContentMutations(): void {
    if (this.destroyed) {
      return;
    }

    const MutationObserverConstructor =
      this.document.defaultView?.MutationObserver;

    if (
      !this.hasUnseenBadgeTargets ||
      !MutationObserverConstructor ||
      !this.document.body
    ) {
      this.mutationObserver?.disconnect();
      this.observedContent = undefined;

      return;
    }

    if (!this.mutationObserver) {
      this.mutationObserver = new MutationObserverConstructor((records) => {
        if (this.observedContent) {
          if (records.some((record) => !this.isRendererOnlyMutation(record))) {
            this.scheduleRender();
          }

          return;
        }

        if (
          records.some((record) => this.containsContentRoot(record.addedNodes))
        ) {
          this.observeContentMutations();
          this.scheduleRender();
        }
      });
    }

    const content = this.document.querySelector<HTMLElement>('.content');

    if (content === this.observedContent) {
      return;
    }

    this.mutationObserver.disconnect();
    this.observedContent = content ?? undefined;
    this.mutationObserver.observe(content ?? this.document.body, {
      childList: true,
      subtree: Boolean(content),
    });
  }

  private containsContentRoot(nodes: NodeList): boolean {
    return Array.from(nodes).some(
      (node) =>
        node.nodeType === 1 &&
        ((node as Element).matches('.content') ||
          (node as Element).querySelector('.content') !== null),
    );
  }

  private scheduleRender(): void {
    if (
      !this.initialized ||
      this.destroyed ||
      this.renderCancellation !== undefined
    ) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.renderCancellation = this.scheduleWork(() => {
        this.renderCancellation = undefined;

        if (this.destroyed) {
          return;
        }

        this.renderBadges();
      });
    });
  }

  private renderBadges(): void {
    if (this.destroyed) {
      return;
    }

    const unseenFeatures = this.announcementStore.unseenFeatures();
    const desired = this.hasUnseenBadgeTargets
      ? badgeTargetResolver.getDesiredBadges(this.document, unseenFeatures)
      : new Map<HTMLElement, Map<string, BadgeTarget>>();

    this.badges.forEach((hostRecords, host) => {
      const desiredForHost = desired.get(host);
      hostRecords.forEach((record, featureKey) => {
        const target = desiredForHost?.get(featureKey);

        if (!target || !host.isConnected) {
          destroyBadge(record);
          hostRecords.delete(featureKey);
        } else {
          updateBadge(record, target, (key) => this.transloco.translate(key));
        }
      });

      if (hostRecords.size === 0) {
        this.badges.delete(host);
      }
    });

    desired.forEach((targets, host) => {
      let hostRecords = this.badges.get(host);

      if (!hostRecords) {
        hostRecords = new Map<string, BadgeRecord>();
        this.badges.set(host, hostRecords);
      }

      targets.forEach((target, featureKey) => {
        if (!hostRecords.has(featureKey)) {
          hostRecords.set(
            featureKey,
            createBadge(
              this.document,
              host,
              target,
              this.createTooltipId(),
              (key) => this.transloco.translate(key),
              (badge, tooltip) =>
                showBadgeTooltip(badge, tooltip, this.document.defaultView),
              hideBadgeTooltip,
              (event) => this.onBadgeClick(event, target.feature),
            ),
          );
        }
      });
    });
  }

  private createTooltipId(): string {
    this.tooltipSequence += 1;

    return `feature-announcement-tooltip-${this.tooltipSequence}`;
  }

  private hideTooltips(): void {
    this.forEachBadgeRecord((record) => hideBadgeTooltip(record.tooltip));
  }

  private scheduleTooltipRefresh(): void {
    if (this.destroyed || this.tooltipCancellation !== undefined) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.tooltipCancellation = this.scheduleWork(() => {
        this.tooltipCancellation = undefined;

        if (this.destroyed) {
          return;
        }

        this.forEachBadgeRecord((record) => {
          if (!record.tooltip.hidden) {
            showBadgeTooltip(
              record.badge,
              record.tooltip,
              this.document.defaultView,
            );
          }
        });
      });
    });
  }

  private isRendererOnlyMutation(record: MutationRecord): boolean {
    const changedNodes = [
      ...Array.from(record.addedNodes),
      ...Array.from(record.removedNodes),
    ];

    return (
      changedNodes.length > 0 &&
      changedNodes.every(
        (node) =>
          node.nodeType === 1 &&
          (node as Element).matches(
            '.feature-announcement-dom-badge, .feature-announcement-dom-badge__tooltip',
          ),
      )
    );
  }

  private scheduleWork(callback: () => void): () => void {
    const window = this.document.defaultView;

    if (window?.requestAnimationFrame && window.cancelAnimationFrame) {
      let cancelled = false;
      const frame = window.requestAnimationFrame(() => {
        if (!cancelled) {
          callback();
        }
      });

      return () => {
        cancelled = true;
        window.cancelAnimationFrame(frame);
      };
    }

    let cancelled = false;
    const timeout = setTimeout(() => {
      if (!cancelled) {
        callback();
      }
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }

  private cancelScheduledWork(cancellation: (() => void) | undefined): void {
    cancellation?.();
  }

  private forEachBadgeRecord(callback: (record: BadgeRecord) => void): void {
    this.badges.forEach((records) =>
      records.forEach((record) => callback(record)),
    );
  }

  private onBadgeClick(event: MouseEvent, feature: FeatureAnnouncement): void {
    event.preventDefault();
    event.stopPropagation();
    this.ngZone.run(() => this.ctaService.execute(feature));
  }
}
